/**
 * Black Matte VTC - Partner Bookings List
 * Pure view layer - ALL data from API
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { partnerService } from '../../services/partnerService';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Plus,
  Search,
  Calendar,
  MapPin,
  User,
  AlertTriangle,
  Eye,
  RefreshCw
} from 'lucide-react';
import { Input } from '../../components/ui/input';

// Status badge - pure display
const BookingStatusBadge = ({ status }) => {
  const statusConfig = {
    confirmed: { label: 'Confirmed', className: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
    completed: { label: 'Completed', className: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
    cancelled: { label: 'Cancelled', className: 'bg-red-500/10 text-red-400 border-red-500/20' },
    in_progress: { label: 'In Progress', className: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
    pending: { label: 'Pending', className: 'bg-neutral-500/10 text-neutral-400 border-neutral-500/20' },
  };
  
  const config = statusConfig[status] || statusConfig.pending;
  
  return (
    <Badge variant="outline" className={config.className}>
      {config.label}
    </Badge>
  );
};

const PartnerBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [balanceData, setBalanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch data from API
  const fetchData = async () => {
    setLoading(true);
    try {
      const status = statusFilter === 'all' ? null : statusFilter;
      const [bookingsRes, balanceRes] = await Promise.all([
        partnerService.getBookings(100, status),
        partnerService.getBalance()
      ]);
      setBookings(bookingsRes.bookings || []);
      setBalanceData(balanceRes);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  // Filter by search term (client-side search on existing data)
  const filteredBookings = bookings.filter(booking => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      booking.booking_reference?.toLowerCase().includes(search) ||
      booking.guest_name?.toLowerCase().includes(search) ||
      booking.pickup_address?.toLowerCase().includes(search) ||
      booking.destination_address?.toLowerCase().includes(search)
    );
  });

  // Blocking check from API
  const isBlocked = balanceData?.has_overdue_invoices || !balanceData?.booking_allowed;

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-400 mb-4">{error}</p>
          <Button onClick={fetchData} variant="outline">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="partner-bookings">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Bookings</h1>
          <p className="text-neutral-500">
            Manage your transportation bookings
          </p>
        </div>
        
        <Button 
          asChild
          className="bg-white text-black hover:bg-neutral-200"
          disabled={isBlocked}
          data-testid="new-booking-btn"
        >
          <Link to="/partner/bookings/new">
            <Plus className="h-4 w-4 mr-2" />
            New Booking
          </Link>
        </Button>
      </div>

      {/* Blocking Banner */}
      {isBlocked && (
        <div 
          className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3"
          data-testid="blocked-banner"
        >
          <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-400">New bookings disabled</p>
            <p className="text-xs text-red-400/80 mt-1">
              {balanceData?.has_overdue_invoices
                ? 'Please settle overdue invoices to continue booking.'
                : 'Credit limit reached. Make a payment to continue.'}
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <Card className="bg-neutral-900 border-neutral-800">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500" />
              <Input
                placeholder="Search by reference, guest, address..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 bg-neutral-800 border-neutral-700 text-white"
                data-testid="search-input"
              />
            </div>

            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48 bg-neutral-800 border-neutral-700 text-white" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent className="bg-neutral-800 border-neutral-700">
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>

            {/* Refresh */}
            <Button
              variant="outline"
              size="icon"
              onClick={fetchData}
              disabled={loading}
              className="border-neutral-700 hover:bg-neutral-800"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Bookings Table */}
      <Card className="bg-neutral-900 border-neutral-800">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 w-full bg-neutral-800" />
              ))}
            </div>
          ) : filteredBookings.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-neutral-800 hover:bg-transparent">
                    <TableHead className="text-neutral-400">Reference</TableHead>
                    <TableHead className="text-neutral-400">Guest</TableHead>
                    <TableHead className="text-neutral-400">Route</TableHead>
                    <TableHead className="text-neutral-400">Date/Time</TableHead>
                    <TableHead className="text-neutral-400">Price</TableHead>
                    <TableHead className="text-neutral-400">Status</TableHead>
                    <TableHead className="text-neutral-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBookings.map((booking) => (
                    <TableRow 
                      key={booking.id} 
                      className="border-neutral-800 hover:bg-neutral-800/50"
                      data-testid={`booking-row-${booking.id}`}
                    >
                      <TableCell className="font-medium text-white">
                        {booking.booking_reference}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-neutral-500" />
                          <span className="text-neutral-300">{booking.guest_name || '—'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-[200px]">
                          <div className="flex items-center gap-1 text-xs text-neutral-400">
                            <MapPin className="h-3 w-3" />
                            <span className="truncate">{booking.pickup_address}</span>
                          </div>
                          <div className="flex items-center gap-1 text-xs text-neutral-500 mt-0.5">
                            <MapPin className="h-3 w-3" />
                            <span className="truncate">{booking.destination_address}</span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm text-neutral-300">
                          <Calendar className="h-4 w-4 text-neutral-500" />
                          <span>{booking.date} {booking.time}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium text-white">
                        €{booking.estimated_price?.toFixed(2) || '—'}
                      </TableCell>
                      <TableCell>
                        <BookingStatusBadge status={booking.status} />
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          asChild
                          className="text-neutral-400 hover:text-white"
                        >
                          <Link to={`/partner/bookings/${booking.id}`}>
                            <Eye className="h-4 w-4" />
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12">
              <Calendar className="h-12 w-12 text-neutral-700 mx-auto mb-3" />
              <p className="text-neutral-500">
                {searchTerm ? 'No bookings match your search' : 'No bookings yet'}
              </p>
              {!searchTerm && !isBlocked && (
                <Button 
                  asChild 
                  size="sm" 
                  className="mt-4 bg-white text-black hover:bg-neutral-200"
                >
                  <Link to="/partner/bookings/new">Create First Booking</Link>
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Count */}
      <div className="text-sm text-neutral-500">
        Showing {filteredBookings.length} of {bookings.length} bookings
      </div>
    </div>
  );
};

export default PartnerBookings;
